import React, { useEffect, useRef, useState } from "react";
import styles from "./invoice_supplier_section.module.css";
import {
    ActionButton,
    Content,
    Dialog,
    DialogContainer,
    Flex,
    Heading,
    Link,
    ProgressCircle,
    TextField,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import SupplierDroDown from "./formComponents/SupplierDropDown";
import { AIChatIcon } from "../../../../../components/protected/Icons/IconsLib";
import { Button } from "react-bootstrap";
import UserDropDown from "./formComponents/UserDropDown";
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { GetContactRequest } from "../../../../../services/soap/main/requests/GetContactRequest";
import { GetFolderRequest } from "../../../../../services/soap/project/requests/GetFolderRequest";
import { getInvoiceDocumentClass } from "../../../../../shared/invoice/invoiceTypeLogic";

interface IInvoiceSupplierSection {
    invoice: any;
    isEdit: any;
    onChange: (data: any) => void;
}

const InvoiceSupplierSection: React.FC<IInvoiceSupplierSection> = ({
    invoice,
    isEdit,
    onChange,
}) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);
    const [fallbackSupplierName, setFallbackSupplierName] = useState<string>("");
    const hydratedSupplierIdRef = useRef<string>("");
    const hydratingSupplierIdRef = useRef<string>("");
    const { addressTypes } = usePreloadAssets();
    const { store, mainService, projectService } = useDependency();

    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
    const _tt = (key: string) => t(`invoice_common.${key}`, { ns: "invoice" });
    const invoiceClass = getInvoiceDocumentClass(invoice);
    const supplierFpaTypeCategory =
        invoiceClass === "sales"
            ? "com.atollon.project.fpatypecategory.organization"
            : "com.atollon.invoice.fpatypecategory.supplier";
    const organizationDefaults = store?.session?.organization?.DEFAULTS?.DEFAULT;
    const organizationDefaultsList = Array.isArray(organizationDefaults)
        ? organizationDefaults
        : organizationDefaults
            ? [organizationDefaults]
            : [];
    const organizationSubjectNode = String(
        organizationDefaultsList.find((x: any) => String(x?.ident ?? "").toUpperCase() === "ORGANIZATIONSUBJECTNODE")?.node ?? ""
    ).trim();
    const resolvedSelectedSupplier =
        invoice?.provider ||
        (invoiceClass === "sales" ? organizationSubjectNode || "0" : "");
    const resolvedSupplierName =
        invoice?.providerName ||
        fallbackSupplierName ||
        (invoiceClass === "sales" && String(resolvedSelectedSupplier) === "0" ? "0" : "");

    const updateInvoice = (updater: any) => {
        onChange((prev: any) => {
            const base = prev || {};
            return typeof updater === "function" ? updater(base) : updater;
        });
    };

    useEffect(() => {
        const supplierId = String(resolvedSelectedSupplier || "").trim();
        if (!supplierId || supplierId === "0") return;
        if (hydratedSupplierIdRef.current === supplierId) return;
        if (hydratingSupplierIdRef.current === supplierId) return;

        const hasSupplierDetails = Boolean(
            invoice?.providerName ||
            invoice?.providerStreet ||
            invoice?.providerCity ||
            invoice?.providerZIP ||
            invoice?.providerRegion ||
            invoice?.providerCountry
        );
        if (hasSupplierDetails) {
            hydratedSupplierIdRef.current = supplierId;
            return;
        }

        hydratingSupplierIdRef.current = supplierId;
        handleSupplierChange({
            id: supplierId,
            name: resolvedSupplierName,
        });
        // This hydrates form fields when supplier comes from defaults/context, not manual selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedSelectedSupplier, resolvedSupplierName]);

    useEffect(() => {
        let active = true;

        const loadFallbackSupplierName = async () => {
            const supplierId = String(resolvedSelectedSupplier || "").trim();
            if (!supplierId || supplierId === "0") {
                if (active) setFallbackSupplierName("");
                return;
            }

            if (String(invoice?.providerName || "").trim()) {
                if (active) setFallbackSupplierName("");
                return;
            }

            const numericId = Number(supplierId);
            if (!Number.isFinite(numericId) || numericId <= 0) {
                if (active) setFallbackSupplierName("");
                return;
            }

            try {
                const folderResponse: any = await projectService.getFolder(
                    new GetFolderRequest(store.Server, store.SessionId, numericId)
                );
                const folder = folderResponse?.FOLDER;
                const company = folder?.CONTACT?.COMPANY;
                const name = String(
                    folder?.name || folder?.name1 || company?.name1 || company?.name || ""
                ).trim();
                if (active) {
                    setFallbackSupplierName(name);
                }
            } catch {
                if (active) {
                    setFallbackSupplierName("");
                }
            }
        };

        loadFallbackSupplierName();
        return () => {
            active = false;
        };
    }, [invoice?.providerName, resolvedSelectedSupplier, projectService, store.Server, store.SessionId]);

    const handleShowMorefields = () => {
        setShowMorefields(!showMorefileds);
    };

    const normalizeAddresses = (addressesWrapper: any): any[] => {
        if (!addressesWrapper) return [];

        let raw = addressesWrapper;

        // ADDRESSES: { ADDRESS: ... }
        if (typeof raw === "object" && !Array.isArray(raw) && raw.ADDRESS) {
            raw = raw.ADDRESS;
        }

        // raw can be array or single object
        const list = Array.isArray(raw) ? raw : [raw];

        // each element can be { ADDRESS: {...} } or direct {...}
        return list.map((x) => x?.ADDRESS ?? x).filter(Boolean);
    };

    const pickAddress = (
        entityType: "COMPANY" | "PERSON",
        addressesWrapper: any
    ) => {
        const list = normalizeAddresses(addressesWrapper);
        if (!list.length) return null;

        const isPrimary = (a: any) => String(a?.isPrimary) === "1";

        const matchByAddressTypes = (a: any) => {
            const typeId = String(a?.type ?? "");
            const at = (addressTypes as any)?.[typeId];
            if (!at) return false;

            // Company: invoiceAddress=1, Person: permanentAddress=1
            return entityType === "COMPANY"
                ? String(at?.invoiceAddress) === "1"
                : String(at?.permanentAddress) === "1";
        };

        // 1) exact match by addressTypes
        const exact = list.find(matchByAddressTypes);
        if (exact) return exact;

        // 2) primary
        const primary = list.find(isPrimary);
        if (primary) return primary;

        // 3) index 0
        return list[0];
    };

    const handleSupplierChange = async (data: any) => {
        if (!data) {
            hydratedSupplierIdRef.current = "";
            hydratingSupplierIdRef.current = "";
            updateInvoice((prev: any) => ({
                ...prev,
                provider: "",
                providerName: "",
                providerStreet: "",
                providerCity: "",
                providerZIP: "",
                providerRegion: "",
                providerCountry: "",
                providerIssuedBy: "",
                providerIssuedByName: "",
            }));
            return;
        }

        const response: any = await mainService.getContact(
            new GetContactRequest(
                store.Server,
                store.SessionId,
                undefined,
                undefined,
                undefined,
                data.id
            )
        );

        let nextValues: any = null;
        if (response?.result === "OK") {
            const isPerson = !!response?.PERSON;
            const entity = isPerson ? response.PERSON : response.COMPANY;
            const entityType: "PERSON" | "COMPANY" = isPerson ? "PERSON" : "COMPANY";
            const address = pickAddress(entityType, entity?.ADDRESSES);

            nextValues = {
                provider: data.id,
                providerName: data?.name ?? entity?.name1 ?? entity?.name ?? "",
                providerStreet: address?.street ?? "",
                providerCity: address?.cityName ?? address?.city ?? "",
                providerZIP: address?.zipName ?? address?.zip ?? "",
                providerRegion: address?.regionName ?? address?.region ?? "",
                providerCountry: address?.countryName ?? address?.country ?? "",
            };
        } else {
            try {
                const folderResponse: any = await projectService.getFolder(
                    new GetFolderRequest(store.Server, store.SessionId, Number(data.id))
                );
                if (folderResponse?.result === "OK") {
                    const folder = folderResponse?.FOLDER;
                    const company = folder?.CONTACT?.COMPANY;
                    const rawAddresses = company?.ADDRESSES?.ADDRESS;
                    const address = Array.isArray(rawAddresses)
                        ? rawAddresses.find((a: any) => String(a?.isPrimary ?? "") === "1") || rawAddresses[0]
                        : rawAddresses || {};

                    nextValues = {
                        provider: String(data.id ?? ""),
                        providerName: data?.name ?? folder?.name ?? folder?.name1 ?? company?.name1 ?? company?.name ?? "",
                        providerStreet: address?.street ?? company?.street ?? "",
                        providerCity: address?.cityName ?? address?.city ?? company?.city ?? "",
                        providerZIP: address?.zipName ?? address?.zip ?? company?.zip ?? "",
                        providerRegion: address?.regionName ?? address?.region ?? company?.region ?? "",
                        providerCountry: address?.countryName ?? address?.country ?? company?.country ?? "",
                    };
                }
            } catch {
                nextValues = null;
            }
        }

        if (!nextValues) {
            hydratedSupplierIdRef.current = String(data.id ?? "");
            hydratingSupplierIdRef.current = "";
            return;
        }

        updateInvoice((prev: any) => ({
            ...prev,
            ...nextValues,
        }));
        hydratedSupplierIdRef.current = String(data.id ?? "");
        hydratingSupplierIdRef.current = "";
    };

    function SupplierModal() {
        return (
            <>
                <Dialog
                    width={{ L: "800px", base: "100%" }}
                    aria-label={_t(isEdit ? "update_invoice" : "add_invoice")}
                    isDismissable
                    onDismiss={() => setShowMorefields(false)}
                >
                    <Heading level={2}>{_t(isEdit ? "supplier" : "supplier")}</Heading>
                    <Content>
                        <Flex
                            direction={{ base: "column", L: "row" }}
                            gap={"size-150"}
                            marginTop={"size-150"}
                        >
                            <SupplierDroDown
                                selectedSupplier={resolvedSelectedSupplier}
                                supplierName={resolvedSupplierName}
                                isEdit={isEdit}
                                fpaTypeCategory={supplierFpaTypeCategory}
                                onChange={(data) => {
                                    handleSupplierChange(data);
                                }}
                            />
                            <TextField
                                width={"100%"}
                                label={_t("street")}
                                value={invoice?.providerStreet}
                                onChange={(e: any) => onChange({ ...invoice, providerStreet: e })}
                            />
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <UserDropDown
                                width={{ base: "100%", L: "52%" }}
                                label={_t("issued_by")}
                                isEdit={isEdit}
                                selectedUser={invoice?.providerIssuedBy}
                                onChange={(data) => {
                                    onChange({
                                        ...invoice,
                                        providerIssuedBy: data != null ? data.id : "",
                                        providerIssuedByName: data != null ? data.name : "",
                                    });
                                }}
                            />
                            <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                                <TextField
                                    width={{ base: "100%", L: "60" }}
                                    label={_t("city")}
                                    value={invoice?.providerCity}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, providerCity: e })
                                    }
                                />
                                <TextField
                                    width={{ base: "100%", L: "40" }}
                                    label={_t("zip_code")}
                                    value={invoice?.providerZIP}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, providerZIP: e })
                                    }
                                />
                            </Flex>
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <TextField
                                width={{ base: "100%", L: "52%" }}
                                label={_t("name")}
                                value={invoice?.providerName || resolvedSupplierName || ""}
                                onChange={(e: any) => onChange({ ...invoice, providerName: e })}
                            />
                            <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                                <TextField
                                    width={{ base: "100%", L: "60" }}
                                    label={_t("region")}
                                    value={invoice?.providerRegion}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, providerRegion: e })
                                    }
                                />
                                <TextField
                                    width={{ base: "100%", L: "40" }}
                                    label={_t("country")}
                                    value={invoice?.providerCountry}
                                    onChange={(e: any) =>
                                        onChange({ ...invoice, providerCountry: e })
                                    }
                                />
                            </Flex>
                        </Flex>
                    </Content>
                </Dialog>

                <Flex
                    UNSAFE_style={{
                        backgroundColor: "white",
                        justifyContent: "end",
                        width: "100%",
                        padding: "1rem",
                        paddingLeft: 55,
                        paddingRight: 50,
                        boxShadow: "0 -1px 1px rgba(0,0,0,0.1)",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 1,
                    }}
                >
                    <Flex gap={"size-200"} UNSAFE_className={styles.button_container}>
                        <Button
                            variant="danger"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_cancel}`}
                        >
                            {_tt("button_cancel")}
                        </Button>
                        <Button
                            type="submit"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_add}`}
                        >
                            {_tt("button_ok")}
                        </Button>
                    </Flex>
                </Flex>
            </>
        );
    }

    return (
        <Flex
            direction={{ base: "column", L: "column" }}
            UNSAFE_className={styles.main_container}
        >
            <Flex justifyContent={"end"} UNSAFE_className={styles.toggle_row}>
                <Link
                    UNSAFE_className={styles.show_more_button}
                    onPress={handleShowMorefields}
                    isQuiet
                >
                    {_tt("show_more")}
                </Link>
            </Flex>

            <SupplierDroDown
                selectedSupplier={resolvedSelectedSupplier}
                supplierName={resolvedSupplierName}
                isEdit={isEdit}
                fpaTypeCategory={supplierFpaTypeCategory}
                onChange={(data) => {
                    handleSupplierChange(data);
                }}
            />

            <DialogContainer onDismiss={() => setShowMorefields(false)}>
                {showMorefileds && SupplierModal()}
            </DialogContainer>
        </Flex>
    );
};

export default InvoiceSupplierSection;

